import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Footer from "@/components/Footer";

const INTERNAL_CASE_STUDY_ID = "global-wealth";
const getHref = (study) => {
  if (EXTERNAL_CASE_STUDIES[study.id]) {
    return { type: "external", href: EXTERNAL_CASE_STUDIES[study.id] };
  }
  if (study.id === INTERNAL_CASE_STUDY_ID) {
    return { type: "internal", href: `/case-studies/${study.id}` };
  }
  return { type: "none", href: null };
};

const IMAGES = {
  finance1: "/assets/finance 1.png",
  finance2: "/assets/finance2.png",
  finance3: "/assets/finance3.png",
  auto: "/assets/cck.png",
  lighting: "/assets/lighting.png",
  dental: "/assets/dental.png",
  interior: "/assets/interior.png",
  medtech: "/assets/medtech.png",
};

const caseStudies = [
  {
    id: "global-wealth",
    category: "Financial Firm",
    title: "Global Wealth Management firm builds a BeSpoke AI Dashboard to automate it workflows end-to-end",
    image: IMAGES.finance1,
    layout: "full",
  },
  {
    id: "wealth-dynamix",
    category: "Financial Firm",
    title: "Wealth Dynamix partners with Compu J to deliver AI enhanced client experiences",
    image: IMAGES.finance2,
    layout: "full",
  },
  {
    id: "family-office",
    category: "Financial Firm",
    title: "Family office strengthens its cyber posture with zero-trust access, endpoint security, and continuous monitoring.",
    image: IMAGES.finance3,
    layout: "half",
  },
  {
    id: "automotive",
    category: "Automotive",
    title: "Multi-site garage network streamlines bookings, job management, and customer updates through one unified platform.",
    image: IMAGES.auto,
    layout: "half",
  },
  {
    id: "dental",
    category: "HealthCare",
    title: "Dental care provider secures patient data with compliance-ready cybersecurity controls across devices and systems.",
    image: IMAGES.dental,
    layout: "half",
  },

  {
  id: "medtech-devices",
  category: "Healthcare",
  title: "MedTech device provider strengthens data security and auditability across connected systems and patient workflows.",
  image: IMAGES.medtech,
  layout: "half",
},

{
  id: "lighting",
  category: "Lighting",
  title: "Leading UK lighting manufacturer streamlines operations with automated scheduling, approvals, and real-time reporting.",
  image: IMAGES.lighting,
  layout: "half",
},

  {
  id: "interior-design",
  category: "Interior Design",
  title: "Interior design studio centralises briefs, client approvals, and project delivery across teams.",
  image: IMAGES.interior,
  layout: "half",
},

];
const EXTERNAL_CASE_STUDIES = {
  "wealth-dynamix":
    "https://wealth-dynamix.com/wealth-dynamix-partners-with-compu-j-to-deliver-ai-enhanced-client-experiences/",
};

function useSectionReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.1 }
    );
    ref.current?.querySelectorAll(".section-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return ref;
}

function FullWidthCard({ study, index }) {
  const link = getHref(study);
  const clickable = link.type !== "none";

  const Wrapper = ({ children }) => {
    const baseClass =
      "section-reveal rounded-xl overflow-hidden block transition-transform";
    const hoverClass = clickable ? " hover:scale-[1.01]" : "";

    const baseStyle = {
      border: "1px solid rgba(255,255,255,0.06)",
      background: "#0A0A0A",
    };

    if (link.type === "internal") {
      return (
        <Link
          to={link.href}
          className={baseClass + hoverClass}
          style={baseStyle}
          data-testid={`case-study-card-${index}`}
        >
          {children}
        </Link>
      );
    }

    if (link.type === "external") {
      return (
        <a
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          className={baseClass + hoverClass}
          style={baseStyle}
          data-testid={`case-study-card-${index}`}
        >
          {children}
        </a>
      );
    }

    return (
      <div
        className={baseClass}
        style={baseStyle}
        data-testid={`case-study-card-${index}`}
      >
        {children}
      </div>
    );
  };

  return (
    <Wrapper>
      <div className="grid grid-cols-1 md:grid-cols-2">
        <div className="relative h-64 md:h-80">
          <img src={study.image} alt={study.title} className="w-full h-full object-cover" />
          <span
            className="absolute top-4 right-4 text-xs px-3 py-1 rounded-full"
            style={{
              background: "rgba(255,255,255,0.1)",
              backdropFilter: "blur(8px)",
              color: "#F5F5F5",
            }}
          >
            {study.category}
          </span>
        </div>

        <div className="p-8 flex flex-col justify-between">
          <h3
            className="text-xl md:text-2xl font-medium leading-snug"
            style={{ fontFamily: "'Lora', serif", color: "#F5F5F5" }}
          >
            {study.title}
          </h3>

          {clickable ? (
            <div className="flex items-center gap-2 mt-6 group">
              <span className="text-sm" style={{ color: "#A1A1AA" }}>
                Read More
              </span>
              <ArrowRight
                size={16}
                style={{ color: "#A1A1AA" }}
                className="group-hover:translate-x-1 transition-transform"
              />
            </div>
          ) : null}
        </div>
      </div>
    </Wrapper>
  );
}

function HalfWidthCard({ study, index }) {
  return (
    <div
      className="section-reveal rounded-xl overflow-hidden block"
      style={{ border: "1px solid rgba(255,255,255,0.06)", background: "#0A0A0A" }}
      data-testid={`case-study-card-${index}`}
    >
      <div className="relative aspect-[16/9] md:aspect-[16/8]">
        <img src={study.image} alt={study.title} className="w-full h-full object-cover" />
        <span
          className="absolute top-4 right-4 text-xs px-3 py-1 rounded-full"
          style={{
            background: "rgba(255,255,255,0.1)",
            backdropFilter: "blur(8px)",
            color: "#F5F5F5",
          }}
        >
          {study.category}
        </span>
      </div>

      <div className="p-7">
        <h3
          className="text-base md:text-xl font-medium leading-snug"
          style={{
            fontFamily: "'Lora', serif",
            color: "#F5F5F5",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {study.title}
        </h3>
      </div>
    </div>
  );
}

export default function CaseStudiesPage() {
  const pageRef = useSectionReveal();
  const fullWidth = caseStudies.filter((c) => c.layout === "full");
  const halfWidth = caseStudies.filter((c) => c.layout === "half");

  return (
    <div ref={pageRef} style={{ backgroundColor: '#050505' }}>
      {/* Hero */}
      <section className="pt-32 pb-16 px-6 md:px-12 lg:px-24" data-testid="case-studies-hero">
        <div className="max-w-4xl">
          <h1
            className="animate-hero-title text-4xl sm:text-5xl lg:text-6xl font-medium leading-tight mb-6"
            style={{ fontFamily: "'Lora', serif", color: '#F5F5F5' }}
            data-testid="case-studies-headline"
          >
            Explore how our key clients are transforming their workflows with AI
          </h1>
          <p className="animate-hero-sub text-sm md:text-base max-w-2xl" style={{ color: '#A1A1AA' }}>
            We tackle our clients' toughest problems with tailored solutions built to integrate, scale, and deliver measurable outcomes.
          </p>
        </div>
      </section>

      {/* Full-width case studies - reduced gap */}
      <section className="mt-8 px-6 md:px-12 lg:px-24" data-testid="case-studies-full">
        <div className="flex flex-col gap-16">
          {fullWidth.map((study, i) => (
            <FullWidthCard key={study.id} study={study} index={i} />
          ))}
        </div>
      </section>

      {/* Half-width grid */}
      <section className="py-16 px-6 md:px-12 lg:px-24" data-testid="case-studies-grid">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {halfWidth.map((study, i) => (
            <HalfWidthCard key={study.id} study={study} index={i + fullWidth.length} />
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
